/**
 * RepConnect - Healthcare Professional Meeting Request Management
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 *
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import {
  CheckCircle, X, Clock, Calendar, User, AlertTriangle,
  RefreshCw, Shield, ShieldCheck, Plus, Bell, MessageSquare,
  Sparkles, ChevronDown, ChevronUp, Copy, Utensils, FileText,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { MeetingStorage } from '../utils/meetingStorage';
import { UserStorage } from '../utils/userStorage';
import { meetingService } from '../utils/supabaseData';
import { supabase } from '../utils/supabase';
import SmartDefaults from '../utils/smartDefaults';
import { InfoTooltip } from '../components/Tooltip';
import { logger } from '../utils/logger';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import ProfilePicture from '../components/ProfilePicture';
import EnhancedMeetingCard from '../components/EnhancedMeetingCard';
import { savedOrdersService } from '../utils/savedOrdersService';
import emailService from '../utils/emailService';
import SavedOrderIndicator from '../components/SavedOrderIndicator';
import { attendeeService } from '../services/attendeeService';

// Helper function to convert 24-hour to 12-hour format
const convertTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

const MeetingRequestsView = ({ currentUser: propCurrentUser, setCurrentView, setSelectedMeetingId, updateNotificationCount, selectedMeetingDate, setSelectedMeetingDate }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [allMeetings, setAllMeetings] = useState([]); // All meetings for calendar display (including past)
  const [notifications, setNotifications] = useState([]);
  const [approvedReps, setApprovedReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // For calendar navigation
  const [viewType, setViewType] = useState('monthly'); // 'daily', 'weekly', 'monthly'
  const [statusFilter, setStatusFilter] = useState('active-pending'); // 'all', 'confirmed', 'active-pending', 'pending', 'cancelled', 'completed'
  const [clickedDay, setClickedDay] = useState(null); // Track clicked day for filtering while keeping calendar visible

  // Smart defaults state
  const [smartSuggestions, setSmartSuggestions] = useState({});
  const [showSmartResponse, setShowSmartResponse] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  // Saved orders state
  const [savedOrdersByRequest, setSavedOrdersByRequest] = useState({});
  const [editedOrders, setEditedOrders] = useState({});
  const [updateSavedOrder, setUpdateSavedOrder] = useState({});

  // Blocked periods state
  const [blockedPeriods, setBlockedPeriods] = useState([]);

  useEffect(() => {
    initializeView();
  }, [propCurrentUser]);

  // Mark cancellations as acknowledged when user visits this view
  useEffect(() => {
    if (currentUser) {
      // Update the last cancellation check timestamp
      localStorage.setItem(`last_cancellation_check_${currentUser.id}`, new Date().toISOString());

      // Trigger notification count update in parent
      if (updateNotificationCount) {
        // Small delay to ensure localStorage is written
        setTimeout(() => updateNotificationCount(), 100);
      }
    }
  }, [currentUser, upcomingMeetings]);

  // Scroll to upcoming meetings section when a specific date is selected
  useEffect(() => {
    if (selectedMeetingDate && upcomingMeetings.length > 0) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        const upcomingSection = document.querySelector('[data-section="upcoming-meetings"]');
        if (upcomingSection) {
          upcomingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [selectedMeetingDate]);

  // Load blocked periods for the customer
  useEffect(() => {
    const loadBlockedPeriods = async () => {
      if (!currentUser) {
        setBlockedPeriods([]);
        return;
      }

      try {
        // First, get the user's current locations to filter out orphaned availability records
        const { data: customerLocations, error: locError } = await supabase
          .from('customer_locations')
          .select('location_id')
          .eq('customer_id', currentUser.id);

        if (locError) {
          logger.warn('MeetingRequestsView: Error fetching customer locations', { error: locError.message });
        }

        // Create a Set of valid location IDs (plus null for general availability)
        const validLocationIds = new Set([null]);
        if (customerLocations) {
          customerLocations.forEach(loc => {
            if (loc.location_id) {
              validLocationIds.add(loc.location_id);
            }
          });
        }

        // Fetch ALL blocked periods from all provider_availability records for this user
        // This includes both location-specific and general (location_id=null) records
        const { data, error } = await supabase
          .from('provider_availability')
          .select('blocked_periods, location_id')
          .eq('provider_id', currentUser.id);

        if (error) {
          logger.error('MeetingRequestsView: Error fetching blocked periods', { error: error.message });
          setBlockedPeriods([]);
          return;
        }

        // Combine blocked periods from valid records only (general + current locations)
        // Skip orphaned records where location was deleted
        // Use a Map to deduplicate by blocked period ID
        const blockedPeriodsMap = new Map();
        if (data && data.length > 0) {
          data.forEach(record => {
            // Skip orphaned records - only include if location_id is null (general) or exists in customer_locations
            if (!validLocationIds.has(record.location_id)) {
              logger.debug('MeetingRequestsView: Skipping orphaned availability record', { location_id: record.location_id });
              return;
            }

            if (record.blocked_periods && record.blocked_periods.length > 0) {
              record.blocked_periods.forEach(period => {
                // Use period.id as key to avoid duplicates
                if (period.id && !blockedPeriodsMap.has(period.id)) {
                  blockedPeriodsMap.set(period.id, period);
                } else if (!period.id) {
                  // If no ID, use a composite key
                  const key = `${period.startDate}-${period.endDate}-${period.title}`;
                  if (!blockedPeriodsMap.has(key)) {
                    blockedPeriodsMap.set(key, period);
                  }
                }
              });
            }
          });
        }

        const blockedPeriodsData = Array.from(blockedPeriodsMap.values());
        setBlockedPeriods(blockedPeriodsData);
      } catch (error) {
        logger.error('MeetingRequestsView: Error in loadBlockedPeriods', { error: error.message });
        setBlockedPeriods([]);
      }
    };

    loadBlockedPeriods();
  }, [currentUser]);

  const initializeView = async () => {
    setLoading(true);
    try {
      // Use prop user if available, otherwise fallback to UserStorage
      const user = propCurrentUser || UserStorage.getCurrentUser();

      // Check both userType (camelCase) and user_type (underscore) formats
      const userType = user?.userType || user?.user_type;

      if (!user) {

        setLoading(false);
        return;
      }

      // Skip user type check here since App.js already handles access control

      setCurrentUser(user);

      // Load approved representatives list
      loadApprovedReps(user.id);

      // Load meeting requests, upcoming meetings, and notifications
      await loadMeetingRequests(user.id);
      await loadUpcomingMeetings(user.id);
      await loadNotifications(user.id);
    } catch (error) {
      logger.error('Error initializing meeting requests view', { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedReps = async (userId) => {
    try {
      // Load from Supabase
      const reps = await preApprovedRepsService.getPreApprovedReps(userId);
      const transformedReps = reps.map(rep => ({
        id: rep.salesRepId,
        name: rep.salesRepName || 'Unknown',
        email: rep.salesRepEmail || '',
        company: rep.salesRepCompany || 'Unknown Company',
        approvedAt: rep.approvedAt
      }));
      setApprovedReps(transformedReps);

      // Also save to localStorage for fallback
      localStorage.setItem(`approved_reps_${userId}`, JSON.stringify(transformedReps));
    } catch (error) {
      logger.error('Error loading approved reps from Supabase', { error: error.message });
      // Fallback to localStorage
      try {
        const approvedRepsData = localStorage.getItem(`approved_reps_${userId}`);
        if (approvedRepsData) {
          setApprovedReps(JSON.parse(approvedRepsData));
        }
      } catch (localError) {
        logger.error('Error loading approved reps from localStorage', { error: localError.message });
      }
    }
  };

  const saveApprovedReps = (userId, reps) => {
    try {
      localStorage.setItem(`approved_reps_${userId}`, JSON.stringify(reps));
      setApprovedReps(reps);
    } catch (error) {
      logger.error('Error saving approved reps', { error: error.message });
    }
  };

  const loadMeetingRequests = async (userId) => {
    try {

      // Try Supabase first - prioritize Supabase data completely
      let pendingRequests = [];
      let usingSupabaseForRequests = false;

      try {
        // Check if user is office staff acting on behalf of a physician
        // If office staff, use the physician's ID they're managing
        const effectiveUserId = currentUser?.delegatingFor?.id || userId;

        // Get all meetings where user is an attendee (includes invited physicians)
        const userMeetings = await attendeeService.getUserMeetings(effectiveUserId);

        // Extract the meetings and filter for pending status
        // getUserMeetings returns attendee records with nested meeting data
        pendingRequests = userMeetings
          .filter(attendeeRecord => attendeeRecord.meetings && attendeeRecord.meetings.status === 'pending')
          .map(attendeeRecord => ({
            ...attendeeRecord.meetings,
            // Add attendee-specific fields
            user_rsvp_status: attendeeRecord.rsvp_status,
            user_is_organizer: attendeeRecord.is_organizer,
            user_attendee_id: attendeeRecord.id
          }));

        usingSupabaseForRequests = true;

      } catch (supabaseError) {
        logger.warn('Supabase meeting requests load failed, trying localStorage fallback', { error: supabaseError.message });
        // Only use localStorage if Supabase completely fails
        pendingRequests = MeetingStorage.getMeetingRequestsForProvider(userId);

      }

      // Transform Supabase format to UI format and enrich requests with rep data
      // Fetch sales rep details for each request
      const enrichedRequests = await Promise.all(pendingRequests.map(async request => {
        let salesRepName = 'Unknown Sales Rep';
        let salesRepEmail = '';
        let salesRepPhone = '';
        let salesRepCompany = 'Company Not Specified';
        let salesRepProfilePicture = null;

        // Fetch sales rep data from user_profiles if we have a sales_rep_id
        if (request.sales_rep_id) {
          try {
            const { supabase } = await import('../utils/supabase');
            const { data: salesRepData, error } = await supabase
              .from('user_profiles')
              .select('first_name, last_name, email, phone, company, profile_picture')
              .eq('id', request.sales_rep_id)
              .single();

            if (salesRepData && !error) {
              salesRepName = `${salesRepData.first_name || ''} ${salesRepData.last_name || ''}`.trim() || salesRepData.email || 'Unknown Sales Rep';
              salesRepEmail = salesRepData.email || '';
              salesRepPhone = salesRepData.phone || '';
              salesRepCompany = salesRepData.company || 'Company Not Specified';
              salesRepProfilePicture = salesRepData.profile_picture || null;
            }
          } catch (err) {
            logger.warn('Could not fetch sales rep data for request', { requestId: request.id, error: err.message });
          }
        }

        // Transform Supabase format to localStorage format for UI compatibility
        const transformedRequest = {
          ...request,
          // Map Supabase fields to expected UI fields
          id: request.id,
          date: request.meeting_date || request.date,
          time: request.start_time ? convertTo12Hour(request.start_time) : request.time,
          salesRepId: request.sales_rep_id || request.salesRepId,
          customerId: request.customer_id || request.customerId,
          salesRepName,
          salesRepEmail,
          salesRepPhone,
          salesRepCompany,
          sales_rep_profile_picture: salesRepProfilePicture,
          status: request.status || 'pending'
        };

        return {
          ...transformedRequest,
          requestId: request.id,
          products: request.products || ['Product information not provided'],
          territory: request.territory || 'Territory not specified',
          specialRequests: request.notes || '',
          restaurant: request.restaurant_name || request.restaurant || '',
          bookingNotes: request.booking_notes || '',
          isUrgent: isRequestUrgent(request)
        };
      }));

      setRequests(enrichedRequests);

      // Generate smart suggestions for each request
      const suggestions = {};
      enrichedRequests.forEach(request => {
        suggestions[request.requestId] = {
          contextual: SmartDefaults.getContextualSuggestions(userId, request),
          templates: SmartDefaults.getMeetingResponseTemplates(userId, request),
          timeSlots: SmartDefaults.getSuggestedTimeSlots(userId, request.date)
        };
      });
      setSmartSuggestions(suggestions);

      // Load saved orders for each request's restaurant
      await loadSavedOrdersForRequests(userId, enrichedRequests);

    } catch (error) {
      logger.error('Error loading meeting requests', { error: error.message });
      setSaveStatus('Error loading meeting requests. Please refresh the page.');
      setTimeout(() => setSaveStatus(''), 4000);
    }
  };

  const loadSavedOrdersForRequests = async (userId, requests) => {
    try {
      const savedOrdersMap = {};

      for (const request of requests) {
        // Get restaurant identifier - prefer yelp_business_id, fallback to name
        const restaurantId = request.restaurant_yelp_id || null;
        const restaurantName = request.restaurant || request.restaurant_name;

        if (!restaurantName) continue;

        // Fetch saved orders for this restaurant
        // Use restaurant_yelp_id if available, otherwise fallback to restaurant name
        const searchKey = restaurantId || restaurantName;
        const orders = await savedOrdersService.getSavedOrdersByRestaurant(
          userId,
          searchKey
        );

        if (orders && orders.length > 0) {
          // Use the first saved order as default (most recently used or created)
          savedOrdersMap[request.requestId] = {
            orders: orders,
            defaultOrder: orders[0]
          };
        }
      }

      setSavedOrdersByRequest(savedOrdersMap);
      logger.debug('Loaded saved orders for requests', { count: Object.keys(savedOrdersMap).length });
    } catch (error) {
      logger.error('Error loading saved orders for requests', { error: error.message });
      // Non-critical, don't show error to user
    }
  };

  const loadUpcomingMeetings = async (userId) => {
    try {
      // Try Supabase first - prioritize Supabase data completely
      let allMeetings = [];
      let usingSupabase = false;

      try {
        // Check if user is office staff acting on behalf of a physician
        // If office staff, use the physician's ID they're managing
        const effectiveUserId = currentUser?.delegatingFor?.id || userId;

        // Use meetingService.getAllMeetings which uses RPC function to bypass RLS
        // This ensures we get ALL meetings where user is an attendee, not just where they're customer_id
        const userMeetings = await meetingService.getAllMeetings(effectiveUserId, 'customer');

        // Meetings are already in the correct format from meetingService
        allMeetings = userMeetings || [];
        usingSupabase = true;

      } catch (supabaseError) {
        logger.warn('Supabase meeting load failed, trying localStorage fallback', { error: supabaseError.message });
        // Only use localStorage if Supabase completely fails
        allMeetings = MeetingStorage.getMeetingsForUser(userId, 'customer');
      }

      // Transform Supabase data to match expected UI format for upcoming meetings
      const transformedMeetings = usingSupabase ? await Promise.all(allMeetings.map(async meeting => {
        let salesRepName = 'Sales Rep';
        let salesRepProfilePicture = null;

        // Fetch sales rep name from user_profiles if we have a sales_rep_id
        if (meeting.sales_rep_id) {
          try {
            const { supabase } = await import('../utils/supabase');
            const { data: salesRepData, error } = await supabase
              .from('user_profiles')
              .select('first_name, last_name, email, id, profile_picture')
              .eq('id', meeting.sales_rep_id)
              .single();

            if (salesRepData && !error) {
              salesRepName = `${salesRepData.first_name || ''} ${salesRepData.last_name || ''}`.trim() || salesRepData.email || 'Sales Rep';
              salesRepProfilePicture = salesRepData.profile_picture || null;
            }
          } catch (err) {
            logger.warn('Could not fetch sales rep name for meeting', { meetingId: meeting.id, error: err.message });
          }
        }

        return {
          ...meeting,
          salesRep: salesRepName, // EnhancedMeetingCard expects salesRep field for customer view
          salesRepName, // Keep for backward compatibility
          sales_rep_profile_picture: salesRepProfilePicture,
          // Extract restaurant name (if available in the meeting data)
          restaurant: meeting.restaurant_name || meeting.restaurant || 'Restaurant TBD',
          // Keep both date formats for compatibility
          date: meeting.meeting_date || meeting.date,
          time: meeting.start_time ? convertTo12Hour(meeting.start_time) : meeting.time
        };
      })) : allMeetings;

      // Store all meetings for calendar display (filtering happens via statusFilter)
      const allMeetingsForCalendar = transformedMeetings
        .sort((a, b) => {
          const dateA = new Date(a.meeting_date || a.date);
          const dateB = new Date(b.meeting_date || b.date);
          return dateA - dateB;
        });
      setAllMeetings(allMeetingsForCalendar);

      // Filter for upcoming meetings (not cancelled or declined)
      // Include today's meetings by comparing dates only (not time)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of day for comparison

      const upcoming = transformedMeetings
        .filter(meeting => {
          // Handle both Supabase format (meeting_date) and localStorage format (date)
          const dateString = meeting.meeting_date || meeting.date;
          let meetingDate;

          // Parse date as local to avoid timezone issues
          if (dateString.includes('-') && dateString.split('-').length === 3) {
            const [year, month, day] = dateString.split('-');
            meetingDate = new Date(year, month - 1, day);
          } else {
            meetingDate = new Date(dateString);
          }

          meetingDate.setHours(0, 0, 0, 0); // Reset to start of day for comparison

          // Include meetings from today onwards
          return meetingDate >= today &&
                 meeting.status !== 'cancelled' &&
                 meeting.status !== 'declined';
        })
        .sort((a, b) => {
          // Sort by date, then by time
          const dateA = new Date(a.meeting_date || a.date);
          const dateB = new Date(b.meeting_date || b.date);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
          }
          // If same date, sort by time
          // Handle both Supabase format (start_time) and localStorage format (time)
          const timeA = a.start_time ?
            MeetingStorage.timeToMinutes(convertTo12Hour(a.start_time)) :
            MeetingStorage.timeToMinutes(a.time);
          const timeB = b.start_time ?
            MeetingStorage.timeToMinutes(convertTo12Hour(b.start_time)) :
            MeetingStorage.timeToMinutes(b.time);
          return timeA - timeB;
        });

      setUpcomingMeetings(upcoming);

    } catch (error) {
      logger.error('Error loading upcoming meetings', { error: error.message });
      setSaveStatus('Error loading upcoming meetings. Please refresh the page.');
      setTimeout(() => setSaveStatus(''), 4000);
    }
  };

  const loadNotifications = async (userId) => {
    try {
      // Get unread notifications for this user (excluding meeting requests which are handled separately)
      const userNotifications = MeetingStorage.getNotificationsForUser(userId, 'customer');
      const unreadNotifications = userNotifications.filter(n =>
        !n.read && n.type !== 'meeting_request'
      );

      setNotifications(unreadNotifications);
    } catch (error) {
      logger.error('Error loading notifications', { error: error.message });
      setSaveStatus('Error loading notifications. Please refresh the page.');
      setTimeout(() => setSaveStatus(''), 4000);
    }
  };

  const isRequestUrgent = (request) => {
    const requestDate = new Date(request.date);
    const now = new Date();
    const hoursUntilMeeting = (requestDate - now) / (1000 * 60 * 60);
    return hoursUntilMeeting <= 48; // Urgent if within 48 hours
  };

  const isRepApproved = (repEmail, repName) => {
    return approvedReps.some(rep =>
      rep.email === repEmail ||
      (rep.name === repName && repName !== 'Unknown Sales Rep')
    );
  };

  const addToApprovedList = async (request) => {
    try {
      // Add to Supabase pre-approved reps
      const result = await preApprovedRepsService.addPreApprovedRep(
        currentUser.id,
        request.salesRepId,
        null // no notes
      );

      if (result) {
        // Also update local state for immediate UI feedback
        const newApprovedRep = {
          id: request.salesRepId,
          name: request.salesRepName,
          email: request.salesRepEmail,
          company: request.salesRepCompany,
          products: request.products,
          territory: request.territory,
          approvedAt: new Date().toISOString(),
          approvedBy: currentUser.id
        };

        const updatedApprovedReps = [...approvedReps, newApprovedRep];
        saveApprovedReps(currentUser.id, updatedApprovedReps);

        setSaveStatus(`${request.salesRepName} added to approved representatives list.`);

        // Reload approved reps from Supabase
        loadApprovedReps(currentUser.id);
      } else {
        setSaveStatus('Could not add representative to approved list.');
      }
    } catch (error) {
      logger.error('Error adding to approved list', { error: error.message });
      setSaveStatus('Error adding representative to approved list.');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const removeFromApprovedList = async (repEmail, repName) => {
    try {
      // Find the rep ID
      const rep = approvedReps.find(r => r.email === repEmail || r.name === repName);
      if (!rep) {
        setSaveStatus('Representative not found in approved list.');
        setTimeout(() => setSaveStatus(''), 3000);
        return;
      }

      // Remove from Supabase
      await preApprovedRepsService.removePreApprovedRep(currentUser.id, rep.id);

      // Update local state
      const updatedApprovedReps = approvedReps.filter(r =>
        r.email !== repEmail && r.name !== repName
      );
      setApprovedReps(updatedApprovedReps);
      saveApprovedReps(currentUser.id, updatedApprovedReps);

      setSaveStatus('Representative removed from approved list.');

      // Reload from Supabase to ensure sync
      await loadApprovedReps(currentUser.id);
    } catch (error) {
      logger.error('Error removing from approved list', { error: error.message });
      setSaveStatus('Error removing representative from approved list.');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleApproveRequest = async (requestId) => {
    setActionInProgress(requestId);
    try {
      // Handle saved order updates if customer made changes
      if (updateSavedOrder[requestId] && editedOrders[requestId] !== undefined) {
        const savedOrderData = savedOrdersByRequest[requestId];
        if (savedOrderData && savedOrderData.defaultOrder) {
          try {
            await savedOrdersService.updateSavedOrder(
              savedOrderData.defaultOrder.id,
              { order_items: editedOrders[requestId] }
            );
            logger.debug('Updated saved order on meeting approval', {
              orderId: savedOrderData.defaultOrder.id
            });
          } catch (orderError) {
            logger.error('Error updating saved order', { error: orderError.message });
            // Don't fail the approval if order update fails
          }
        }
      }

      // Try Supabase first
      let updatedMeeting = null;
      try {
        logger.debug('🔄 Attempting Supabase update with data:', {
          requestId,
          updateData: {
            status: 'confirmed',
            confirmed_by: currentUser.id,
            confirmed_at: new Date().toISOString()
          },
          currentUser
        });

        updatedMeeting = await meetingService.updateMeeting(requestId, {
          status: 'confirmed',
          confirmed_by: currentUser.id,
          confirmed_at: new Date().toISOString()
        });

      } catch (supabaseError) {
        logger.error('Detailed Supabase approval error', {
          error: supabaseError,
          message: supabaseError.message,
          details: supabaseError.details,
          hint: supabaseError.hint,
          code: supabaseError.code
        });
        logger.warn('Supabase approval failed, trying localStorage fallback', { error: supabaseError.message });
        // Fallback to localStorage
        updatedMeeting = MeetingStorage.approveMeetingRequest(requestId, {
          confirmedBy: currentUser.id,
          confirmedAt: new Date().toISOString()
        });
      }

      if (updatedMeeting) {
        const statusMessage = updateSavedOrder[requestId]
          ? 'Meeting approved and saved order updated!'
          : 'Meeting request approved successfully!';
        setSaveStatus(statusMessage);

        // Send email notification to sales rep
        const request = requests.find(r => r.requestId === requestId || r.id === requestId);
        if (request && request.salesRepEmail) {
          try {
            await emailService.sendMeetingResponseEmail({
              salesRepEmail: request.salesRepEmail,
              salesRepName: request.salesRepName,
              customerName: currentUser.first_name && currentUser.last_name
                ? `${currentUser.first_name} ${currentUser.last_name}`
                : currentUser.email,
              meetingDate: request.date,
              meetingTime: request.time,
              restaurantName: request.restaurant,
              status: 'approved',
              responseMessage: null // No custom message for simple approve
            });
            logger.debug('Meeting approval email sent to sales rep');
          } catch (emailError) {
            logger.error('Failed to send meeting approval email', { error: emailError.message });
            // Don't fail the approval if email fails
          }
        }

        // Clear edited order state for this request
        const newEditedOrders = { ...editedOrders };
        delete newEditedOrders[requestId];
        setEditedOrders(newEditedOrders);

        const newUpdateFlags = { ...updateSavedOrder };
        delete newUpdateFlags[requestId];
        setUpdateSavedOrder(newUpdateFlags);

        await loadMeetingRequests(currentUser.id);
        await loadUpcomingMeetings(currentUser.id);
        await loadNotifications(currentUser.id);
        if (updateNotificationCount) {
          updateNotificationCount();
        }
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        throw new Error('Failed to approve meeting');
      }
    } catch (error) {
      logger.error('Error approving request', { error: error.message });
      setSaveStatus('Error approving meeting request. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeclineRequest = async (requestId, repName) => {
    setActionInProgress(requestId);
    try {

      // Try Supabase first
      let updatedMeeting = null;
      try {
        updatedMeeting = await meetingService.updateMeeting(requestId, {
          status: 'declined',
          declined_by: currentUser.id,
          declined_at: new Date().toISOString(),
          decline_reason: 'Request declined by healthcare provider'
        });

      } catch (supabaseError) {
        logger.warn('Supabase decline failed, trying localStorage fallback', { error: supabaseError.message });
        // Fallback to localStorage
        updatedMeeting = MeetingStorage.declineMeetingRequest(requestId, {
          declinedBy: currentUser.id,
          reason: 'Request declined by healthcare provider'
        });
      }

      if (updatedMeeting) {
        setSaveStatus(`Meeting request from ${repName} declined.`);

        // Send email notification to sales rep
        const request = requests.find(r => r.requestId === requestId || r.id === requestId);
        if (request && request.salesRepEmail) {
          try {
            await emailService.sendMeetingResponseEmail({
              salesRepEmail: request.salesRepEmail,
              salesRepName: request.salesRepName,
              customerName: currentUser.first_name && currentUser.last_name
                ? `${currentUser.first_name} ${currentUser.last_name}`
                : currentUser.email,
              meetingDate: request.date,
              meetingTime: request.time,
              restaurantName: request.restaurant,
              status: 'declined',
              responseMessage: null // No custom message for simple decline
            });
            logger.debug('Meeting decline email sent to sales rep');
          } catch (emailError) {
            logger.error('Failed to send meeting decline email', { error: emailError.message });
            // Don't fail the decline if email fails
          }
        }

        await loadMeetingRequests(currentUser.id);
        await loadUpcomingMeetings(currentUser.id);
        await loadNotifications(currentUser.id);
        if (updateNotificationCount) {
          updateNotificationCount();
        }
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        throw new Error('Failed to decline meeting');
      }
    } catch (error) {
      logger.error('Error declining request', { error: error.message });
      setSaveStatus('Error declining meeting request. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAcceptRSVP = async (meeting) => {
    try {
      setActionInProgress(meeting.id);
      // Use effective user ID for office staff delegating for a physician
      const effectiveUserId = currentUser?.delegatingFor?.id || currentUser.id;
      logger.info('Accepting RSVP for meeting', { meetingId: meeting.id, effectiveUserId, staffId: currentUser.id });

      await attendeeService.updateRSVPByUser(effectiveUserId, meeting.id, 'accepted');

      setSaveStatus('RSVP accepted successfully!');
      await loadMeetingRequests(currentUser.id);
      await loadUpcomingMeetings(currentUser.id);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error accepting RSVP', { error: error.message });
      setSaveStatus('Error accepting RSVP. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeclineRSVP = async (meeting) => {
    try {
      setActionInProgress(meeting.id);
      // Use effective user ID for office staff delegating for a physician
      const effectiveUserId = currentUser?.delegatingFor?.id || currentUser.id;
      logger.info('Declining RSVP for meeting', { meetingId: meeting.id, effectiveUserId, staffId: currentUser.id });

      await attendeeService.updateRSVPByUser(effectiveUserId, meeting.id, 'declined');

      setSaveStatus('RSVP declined.');
      await loadMeetingRequests(currentUser.id);
      await loadUpcomingMeetings(currentUser.id);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error declining RSVP', { error: error.message });
      setSaveStatus('Error declining RSVP. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (currentUser) {
      await loadMeetingRequests(currentUser.id);
      await loadUpcomingMeetings(currentUser.id);
      await loadNotifications(currentUser.id);
    }
    setRefreshing(false);
  };

  const markNotificationAsRead = (notificationId) => {
    try {
      MeetingStorage.markNotificationAsRead(notificationId);
      // Remove from local state
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      logger.error('Error marking notification as read', { error: error.message });
    }
  };

  // Helper to parse meeting date
  const parseMeetingDate = (meeting) => {
    const dateString = meeting.meeting_date || meeting.date;
    if (dateString && dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day);
    }
    return new Date(dateString || Date.now());
  };

  // Filter meetings based on view type and status (matching sales rep view)
  const getFilteredMeetings = () => {
    const baseDate = new Date(calendarMonth);
    baseDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    let dateMeetings = [];

    if (viewType === 'daily') {
      const endDate = new Date(baseDate);
      endDate.setHours(23, 59, 59, 999);
      dateMeetings = allMeetings.filter(meeting => {
        const meetingDate = parseMeetingDate(meeting);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= baseDate && meetingDate <= endDate;
      });
    } else if (viewType === 'weekly') {
      // Get start of week (Sunday)
      const startOfWeek = new Date(baseDate);
      const day = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - day);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      dateMeetings = allMeetings.filter(meeting => {
        const meetingDate = parseMeetingDate(meeting);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= startOfWeek && meetingDate <= endOfWeek;
      });
    } else if (viewType === 'monthly') {
      // If a day is clicked in the calendar, filter to that specific day
      if (clickedDay) {
        const clickedDayStart = new Date(clickedDay);
        clickedDayStart.setHours(0, 0, 0, 0);
        const clickedDayEnd = new Date(clickedDay);
        clickedDayEnd.setHours(23, 59, 59, 999);

        dateMeetings = allMeetings.filter(meeting => {
          const meetingDate = parseMeetingDate(meeting);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate >= clickedDayStart && meetingDate <= clickedDayEnd;
        });
      } else {
        // No day clicked - show all meetings for the month
        const startOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
        const endOfMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        dateMeetings = allMeetings.filter(meeting => {
          const meetingDate = parseMeetingDate(meeting);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate >= startOfMonth && meetingDate <= endOfMonth;
        });
      }
    }

    // Auto-mark past confirmed meetings as completed BEFORE applying status filter
    const autoCompletedMeetings = dateMeetings.map(meeting => {
      const meetingDate = parseMeetingDate(meeting);
      const meetingTime = meeting.start_time || meeting.time || '12:00';
      const meetingDateTime = new Date(meetingDate.toDateString() + ' ' + meetingTime);

      // If meeting was confirmed and the date/time has passed, mark as completed
      if (meeting.status === 'confirmed' && meetingDateTime < now) {
        return { ...meeting, status: 'completed' };
      }
      return meeting;
    });

    // Apply status filter AFTER auto-completion
    if (statusFilter === 'all') {
      return autoCompletedMeetings.sort((a, b) => parseMeetingDate(a) - parseMeetingDate(b));
    }

    const filtered = autoCompletedMeetings.filter(meeting => {
      const meetingDate = parseMeetingDate(meeting);
      meetingDate.setHours(0, 0, 0, 0);
      const isFuture = meetingDate >= now;

      if (statusFilter === 'confirmed') {
        return meeting.status === 'confirmed' && isFuture;
      } else if (statusFilter === 'active-pending') {
        return (meeting.status === 'confirmed' || meeting.status === 'pending') && isFuture;
      } else if (statusFilter === 'pending') {
        return meeting.status === 'pending' && isFuture;
      } else if (statusFilter === 'completed') {
        return meeting.status === 'completed';
      } else if (statusFilter === 'cancelled') {
        return meeting.status === 'cancelled' || meeting.status === 'declined';
      }

      return false;
    });

    // Sort: upcoming chronologically, completed reverse chronologically
    return filtered.sort((a, b) => {
      if (statusFilter === 'completed') {
        return parseMeetingDate(b) - parseMeetingDate(a); // Most recent first
      }
      return parseMeetingDate(a) - parseMeetingDate(b); // Soonest first
    });
  };

  // Get date range text for display (matching sales rep view)
  const getDateRangeText = () => {
    const baseDate = new Date(calendarMonth);

    if (viewType === 'daily') {
      return baseDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (viewType === 'weekly') {
      const startOfWeek = new Date(baseDate);
      const day = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - day);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (viewType === 'monthly') {
      return baseDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
    return '';
  };

  // Navigation functions (matching sales rep view)
  const navigateDate = (direction) => {
    const newDate = new Date(calendarMonth);

    if (viewType === 'daily') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewType === 'weekly') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewType === 'monthly') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
      // Clear clicked day when navigating to a different month
      setClickedDay(null);
    }

    setCalendarMonth(newDate);
  };

  // Smart response handlers
  const toggleSmartResponse = (requestId) => {
    setShowSmartResponse(showSmartResponse === requestId ? null : requestId);
    setSelectedTemplate('');
    setCustomMessage('');
  };

  const selectTemplate = (requestId, templateType, templateKey) => {
    const templates = smartSuggestions[requestId]?.templates;
    if (templates && templates[templateType] && templates[templateType][templateKey]) {
      const template = templates[templateType][templateKey];
      setSelectedTemplate(`${templateType}.${templateKey}`);
      setCustomMessage(template);
    }
  };

  const copyTemplate = () => {
    if (customMessage) {
      navigator.clipboard.writeText(customMessage).then(() => {
        setSaveStatus('Message copied to clipboard!');
        setTimeout(() => setSaveStatus(''), 2000);
      }).catch(err => {
        logger.error('Failed to copy text', { error: err.message });
      });
    }
  };

  const handleSmartApprove = async (requestId, message) => {
    setActionInProgress(requestId);
    try {
      // Handle saved order updates if customer made changes
      if (updateSavedOrder[requestId] && editedOrders[requestId] !== undefined) {
        const savedOrderData = savedOrdersByRequest[requestId];
        if (savedOrderData && savedOrderData.defaultOrder) {
          try {
            await savedOrdersService.updateSavedOrder(
              savedOrderData.defaultOrder.id,
              { order_items: editedOrders[requestId] }
            );
            logger.debug('Updated saved order on smart approval', {
              orderId: savedOrderData.defaultOrder.id
            });
          } catch (orderError) {
            logger.error('Error updating saved order', { error: orderError.message });
            // Don't fail the approval if order update fails
          }
        }
      }

      // Try Supabase first
      let updatedMeeting = null;
      try {
        updatedMeeting = await meetingService.updateMeeting(requestId, {
          status: 'confirmed',
          confirmed_by: currentUser.id,
          confirmed_at: new Date().toISOString(),
          response_message: message || 'Meeting approved'
        });

      } catch (supabaseError) {
        logger.warn('Supabase smart approval failed, trying localStorage fallback', { error: supabaseError.message });
        // Fallback to localStorage
        updatedMeeting = MeetingStorage.approveMeetingRequest(requestId, {
          confirmedBy: currentUser.id,
          confirmedAt: new Date().toISOString(),
          responseMessage: message || 'Meeting approved'
        });
      }

      if (updatedMeeting) {
        const statusMessage = updateSavedOrder[requestId]
          ? 'Meeting approved with message and saved order updated!'
          : 'Meeting request approved with personalized message!';
        setSaveStatus(statusMessage);

        // Send email notification to sales rep
        const request = requests.find(r => r.requestId === requestId || r.id === requestId);
        if (request && request.salesRepEmail) {
          try {
            await emailService.sendMeetingResponseEmail({
              salesRepEmail: request.salesRepEmail,
              salesRepName: request.salesRepName,
              customerName: currentUser.first_name && currentUser.last_name
                ? `${currentUser.first_name} ${currentUser.last_name}`
                : currentUser.email,
              meetingDate: request.date,
              meetingTime: request.time,
              restaurantName: request.restaurant,
              status: 'approved',
              responseMessage: message
            });
            logger.debug('Meeting response email sent to sales rep');
          } catch (emailError) {
            logger.error('Failed to send meeting response email', { error: emailError.message });
            // Don't fail the approval if email fails
          }
        }

        // Clear edited order state for this request
        const newEditedOrders = { ...editedOrders };
        delete newEditedOrders[requestId];
        setEditedOrders(newEditedOrders);

        const newUpdateFlags = { ...updateSavedOrder };
        delete newUpdateFlags[requestId];
        setUpdateSavedOrder(newUpdateFlags);

        await loadMeetingRequests(currentUser.id);
        await loadUpcomingMeetings(currentUser.id);
        if (updateNotificationCount) {
          updateNotificationCount();
        }
        setShowSmartResponse(null);
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        throw new Error('Failed to approve meeting');
      }
    } catch (error) {
      logger.error('Error approving request', { error: error.message });
      setSaveStatus('Error approving meeting request. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSmartDecline = async (requestId, message) => {
    setActionInProgress(requestId);
    try {

      // Try Supabase first
      let updatedMeeting = null;
      try {
        updatedMeeting = await meetingService.updateMeeting(requestId, {
          status: 'declined',
          declined_by: currentUser.id,
          declined_at: new Date().toISOString(),
          decline_reason: message || 'Request declined by healthcare provider',
          response_message: message
        });

      } catch (supabaseError) {
        logger.warn('Supabase smart decline failed, trying localStorage fallback', { error: supabaseError.message });
        // Fallback to localStorage
        updatedMeeting = MeetingStorage.declineMeetingRequest(requestId, {
          declinedBy: currentUser.id,
          reason: message || 'Request declined by healthcare provider',
          responseMessage: message
        });
      }

      if (updatedMeeting) {
        setSaveStatus('Meeting request declined with personalized message.');

        // Send email notification to sales rep
        const request = requests.find(r => r.requestId === requestId || r.id === requestId);
        if (request && request.salesRepEmail) {
          try {
            await emailService.sendMeetingResponseEmail({
              salesRepEmail: request.salesRepEmail,
              salesRepName: request.salesRepName,
              customerName: currentUser.first_name && currentUser.last_name
                ? `${currentUser.first_name} ${currentUser.last_name}`
                : currentUser.email,
              meetingDate: request.date,
              meetingTime: request.time,
              restaurantName: request.restaurant,
              status: 'declined',
              responseMessage: message
            });
            logger.debug('Meeting decline email sent to sales rep');
          } catch (emailError) {
            logger.error('Failed to send meeting decline email', { error: emailError.message });
            // Don't fail the decline if email fails
          }
        }

        await loadMeetingRequests(currentUser.id);
        await loadUpcomingMeetings(currentUser.id);
        if (updateNotificationCount) {
          updateNotificationCount();
        }
        setShowSmartResponse(null);
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        throw new Error('Failed to decline meeting');
      }
    } catch (error) {
      logger.error('Error declining request', { error: error.message });
      setSaveStatus('Error declining meeting request. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Check both userType formats and allow access for healthcare providers
  const userType = currentUser?.userType || currentUser?.user_type;

  if (!currentUser || (userType !== 'customer' && userType !== 'office_staff')) {

    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
          <p className="text-red-600">Meeting requests are only available for healthcare providers.</p>
          <p className="text-red-500 text-sm mt-2">Debug: User type "{userType}" not recognized as customer/office_staff</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Calendar</h2>
            <p className="text-gray-600 mt-1">
              Manage meeting requests and view all upcoming meetings
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Status Message */}
        {saveStatus && (
          <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
            saveStatus.includes('Error')
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {saveStatus.includes('Error') ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            <span>{saveStatus}</span>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Bell className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">
                {[
                  requests.length > 0 && `${requests.length} Pending Request${requests.length !== 1 ? 's' : ''}`,
                  notifications.length > 0 && `${notifications.length} Unread Notification${notifications.length !== 1 ? 's' : ''}`,
                  upcomingMeetings.length > 0 && `${upcomingMeetings.length} Upcoming Meeting${upcomingMeetings.length !== 1 ? 's' : ''}`
                ].filter(Boolean).join(', ') || 'No pending items'}
              </p>
              <p className="text-sm text-blue-700">
                {approvedReps.length} Representative{approvedReps.length !== 1 ? 's' : ''} on your approved list
                {requests.length === 0 && ' • No meeting requests at this time'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Portal Calendar View */}
      {(() => {
        // Helper function to get calendar days for a given month
        const getCalendarDays = (date) => {
          const year = date.getFullYear();
          const month = date.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const daysInMonth = lastDay.getDate();
          const startingDayOfWeek = firstDay.getDay();

          const days = [];
          // Add empty cells for days before month starts
          for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
          }
          // Add days of month
          for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
          }
          return days;
        };

        // Helper function to get meetings for a specific date (uses allMeetings to include past meetings)
        const getMeetingsForDate = (dateString) => {
          return allMeetings.filter(meeting => {
            const meetingDateStr = meeting.meeting_date || meeting.date;
            return meetingDateStr === dateString;
          });
        };

        // Helper function to check if a date is within any blocked period
        const getBlockedInfoForDate = (dateString) => {
          if (!blockedPeriods || blockedPeriods.length === 0) {
            return { isBlocked: false, reasons: [] };
          }

          const checkDate = new Date(dateString);
          const reasons = [];

          blockedPeriods.forEach(period => {
            const startDate = new Date(period.startDate);
            const endDate = new Date(period.endDate);
            if (checkDate >= startDate && checkDate <= endDate) {
              reasons.push(period.reason || 'Blocked');
            }
          });

          return {
            isBlocked: reasons.length > 0,
            reasons
          };
        };

        // Navigation function - works with all view types
        const navigateDate = (direction) => {
          const newDate = new Date(calendarMonth);
          if (viewType === 'daily') {
            newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
          } else if (viewType === 'weekly') {
            newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
          } else if (viewType === 'monthly') {
            newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
            // Clear clicked day when navigating to a different month
            setClickedDay(null);
          }
          setCalendarMonth(newDate);
        };

        const goToPreviousMonth = () => navigateDate('prev');
        const goToNextMonth = () => navigateDate('next');

        const goToToday = () => {
          setCalendarMonth(new Date());
          if (viewType === 'monthly') {
            setClickedDay(new Date());
          }
        };

        // Get date range text for display based on view type
        const getDateRangeText = () => {
          const baseDate = new Date(calendarMonth);

          if (viewType === 'daily') {
            return baseDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          } else if (viewType === 'weekly') {
            const startOfWeek = new Date(baseDate);
            const day = startOfWeek.getDay();
            const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
            startOfWeek.setDate(diff);

            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
          } else if (viewType === 'monthly') {
            return baseDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
          }
          return '';
        };

        const monthYear = getDateRangeText();

        return (
          <div className="mt-6 border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden w-full">
            {/* Calendar Header with Navigation - Compact, matching MeetingsView */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 px-2 py-2 sm:p-3">
              <div className="flex items-center justify-between gap-1">
                {/* View selector */}
                <select
                  value={viewType}
                  onChange={(e) => setViewType(e.target.value)}
                  className="px-2 py-1.5 text-xs sm:text-sm font-medium border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 no-touch-target"
                >
                  <option value="daily">Day</option>
                  <option value="weekly">Week</option>
                  <option value="monthly">Month</option>
                </select>

                {/* Navigation */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={goToPreviousMonth}
                    className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors touch-manipulation"
                  >
                    <ChevronRight className="w-5 h-5 rotate-180 text-gray-700" />
                  </button>

                  <h3 className="text-xs sm:text-base font-semibold text-gray-800 text-center min-w-[100px] sm:min-w-[140px]">{monthYear}</h3>

                  <button
                    onClick={goToNextMonth}
                    className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors touch-manipulation"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-700" />
                  </button>
                </div>

                {/* Today button */}
                <button
                  onClick={goToToday}
                  className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation no-touch-target"
                >
                  Today
                </button>
              </div>
            </div>

            {/* Days of Week Header */}
            <div className="grid grid-cols-7 border-b border-gray-200 w-full">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 md:p-3 text-center text-xs font-medium text-gray-600 bg-gray-50 min-w-0">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days Grid - Color-coded by meeting status */}
            <div className="grid grid-cols-7 w-full">
              {getCalendarDays(calendarMonth).map((day, index) => {
                if (!day) {
                  return <div key={index} className="h-12 sm:h-16 md:h-20 min-w-0"></div>;
                }

                // Format date in local timezone (YYYY-MM-DD)
                const dateString = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                const dayMeetings = getMeetingsForDate(dateString);
                const blockedInfo = getBlockedInfoForDate(dateString);

                // Get today's date in local timezone (YYYY-MM-DD format)
                const todayDate = new Date();
                const todayString = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
                const isToday = dateString === todayString;

                const isPast = day < new Date(new Date().toDateString());

                // Count meetings by status
                const confirmedCount = dayMeetings.filter(m => m.status === 'confirmed').length;
                const pendingCount = dayMeetings.filter(m => m.status === 'pending').length;
                const completedCount = dayMeetings.filter(m => m.status === 'completed').length;
                const totalCount = dayMeetings.length;

                // Determine background color based on meetings
                let bgClass = 'bg-white hover:bg-gray-50';
                let textClass = 'text-gray-900';

                if (isPast) {
                  // Past days - show blue if there were meetings, gray if not
                  if (completedCount > 0 || totalCount > 0) {
                    bgClass = 'bg-blue-50 hover:bg-blue-100';
                    textClass = 'text-blue-700';
                  } else {
                    bgClass = 'bg-gray-50';
                    textClass = 'text-gray-400';
                  }
                } else if (totalCount > 0) {
                  if (confirmedCount > 0 && pendingCount === 0) {
                    // All confirmed - green
                    bgClass = 'bg-green-100 hover:bg-green-150';
                    textClass = 'text-green-900';
                  } else if (pendingCount > 0 && confirmedCount === 0) {
                    // All pending - yellow
                    bgClass = 'bg-yellow-100 hover:bg-yellow-150';
                    textClass = 'text-yellow-900';
                  } else if (confirmedCount > 0 && pendingCount > 0) {
                    // Mix of confirmed and pending - orange
                    bgClass = 'bg-orange-100 hover:bg-orange-150';
                    textClass = 'text-orange-900';
                  }
                }

                if (isToday) {
                  textClass += ' font-bold';
                }

                const isClickedDay = clickedDay && clickedDay.toDateString() === day.toDateString();

                // Check if day is in selected week (for weekly view)
                const isInSelectedWeek = viewType === 'weekly' && (() => {
                  const weekStart = new Date(calendarMonth);
                  const dayOfWeek = weekStart.getDay();
                  const diff = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
                  weekStart.setDate(diff);
                  weekStart.setHours(0, 0, 0, 0);

                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);
                  weekEnd.setHours(23, 59, 59, 999);

                  const dayTime = new Date(day).setHours(0, 0, 0, 0);
                  return dayTime >= weekStart.getTime() && dayTime <= weekEnd.getTime();
                })();

                return (
                  <button
                    key={index}
                    onClick={() => {
                      if (viewType === 'daily') {
                        // In daily view, clicking a day updates both calendarMonth and clickedDay
                        setCalendarMonth(new Date(day));
                        setClickedDay(new Date(day));
                      } else if (viewType === 'weekly') {
                        // In weekly view, clicking a day updates calendarMonth to that week
                        setCalendarMonth(new Date(day));
                      } else if (viewType === 'monthly') {
                        // In monthly view, clicking a day switches to daily view for that day
                        setCalendarMonth(new Date(day));
                        setClickedDay(new Date(day));
                        setViewType('daily');
                      }
                      // Scroll to meetings section
                      const upcomingSection = document.querySelector('[data-section="upcoming-meetings"]');
                      if (upcomingSection) {
                        upcomingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className={`h-12 sm:h-16 md:h-20 text-xs sm:text-sm font-medium border-r border-b border-gray-200 transition-all duration-200 relative p-1 sm:p-2 min-w-0 ${bgClass} ${textClass} ${
                      isToday ? 'ring-2 ring-inset ring-blue-600 shadow-md' : ''
                    } ${isClickedDay ? 'ring-2 ring-inset ring-purple-500 shadow-lg' : ''} ${isInSelectedWeek ? 'ring-2 ring-inset ring-purple-500 shadow-lg' : ''}`}
                  >
                    <div className="flex flex-col items-center justify-center h-full min-w-0">
                      <span className="block">{day.getDate()}</span>
                      {totalCount > 0 && (
                        <div className="flex gap-0.5 sm:gap-1 mt-0.5 sm:mt-1 flex-wrap justify-center">
                          {/* Render dots for each meeting type */}
                          {[...Array(confirmedCount)].map((_, i) => (
                            <div key={`confirmed-${i}`} className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-green-600 rounded-full flex-shrink-0" title={`${confirmedCount} confirmed`}></div>
                          ))}
                          {[...Array(pendingCount)].map((_, i) => (
                            <div key={`pending-${i}`} className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-yellow-600 rounded-full flex-shrink-0" title={`${pendingCount} pending`}></div>
                          ))}
                          {[...Array(completedCount)].map((_, i) => (
                            <div key={`completed-${i}`} className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-600 rounded-full flex-shrink-0" title={`${completedCount} completed`}></div>
                          ))}
                        </div>
                      )}
                      {/* Blocked period indicator - show red dot */}
                      {blockedInfo.isBlocked && (
                        <div className="flex gap-0.5 sm:gap-1 mt-0.5 sm:mt-1 flex-wrap justify-center">
                          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-red-600 rounded-full flex-shrink-0" title={blockedInfo.reasons.join(', ')}></div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Calendar Legend */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                  <span className="text-gray-700">Confirmed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
                  <span className="text-gray-700">Pending</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded"></div>
                  <span className="text-gray-700">Mixed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
                  <span className="text-gray-700">Completed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
                  <span className="text-gray-700">No Meetings</span>
                </div>
                {blockedPeriods.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-100 border border-red-300 rounded flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-700">Blocked</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Filter and Summary Section - Below calendar */}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="font-medium">{allMeetings.length} meeting{allMeetings.length !== 1 ? 's' : ''}</span>
          <span className="text-gray-400">|</span>
          <span className="text-green-600">{allMeetings.filter(m => m.status === 'confirmed').length} confirmed</span>
          <span className="text-yellow-600">{allMeetings.filter(m => m.status === 'pending').length} pending</span>
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 no-touch-target"
        >
          <option value="all">All</option>
          <option value="confirmed">Confirmed</option>
          <option value="active-pending">Active + Pending</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Recent Cancellations Alert */}
      {(() => {
        const recentCancellations = upcomingMeetings.filter(meeting => {
          if (meeting.status !== 'cancelled') return false;
          const cancelledAt = meeting.cancelled_at ? new Date(meeting.cancelled_at) : new Date(0);
          // Show cancellations from the last 7 days for visibility
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return cancelledAt > sevenDaysAgo;
        });

        if (recentCancellations.length === 0) return null;

        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-800 mb-2">
                  Recent Cancellations ({recentCancellations.length})
                </h4>
                <div className="space-y-2">
                  {recentCancellations.map(meeting => {
                    const dateString = meeting.meeting_date || meeting.date;
                    let meetingDate;
                    if (dateString.includes('-') && dateString.split('-').length === 3) {
                      const [year, month, day] = dateString.split('-');
                      meetingDate = new Date(year, month - 1, day);
                    } else {
                      meetingDate = new Date(dateString);
                    }

                    return (
                      <div key={meeting.id} className="bg-white rounded-lg p-3 border border-red-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800">
                              {meetingDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              {' at '}
                              {meeting.start_time ? convertTo12Hour(meeting.start_time) : meeting.time}
                            </p>
                            <p className="text-xs text-gray-600">
                              with {meeting.salesRepName || 'Sales Rep'}
                              {meeting.cancelled_at && (
                                <span className="ml-2 text-red-600">
                                  • Cancelled {new Date(meeting.cancelled_at).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                            {meeting.cancellation_reason && (
                              <p className="text-xs text-gray-500 mt-1">
                                Reason: {meeting.cancellation_reason}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              if (setSelectedMeetingId && setCurrentView) {
                                setSelectedMeetingId(meeting.id);
                                setCurrentView('meeting-detail');
                              }
                            }}
                            className="ml-3 px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length > 0 && requests.map((request) => {
            const repApproved = isRepApproved(request.salesRepEmail, request.salesRepName);
            const suggestions = smartSuggestions[request.requestId];
            const showingSmart = showSmartResponse === request.requestId;

            return (
              <div key={request.requestId} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    {/* Meeting Date & Time */}
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <span className="text-lg font-semibold text-gray-800">
                          {(() => {
                            // Parse date as local to avoid timezone shifts
                            const date = new Date(request.date + 'T12:00:00Z');
                            return date.toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              timeZone: 'UTC'
                            });
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-5 h-5 text-gray-600" />
                        <span className="text-lg font-semibold text-gray-800">{request.time}</span>
                      </div>
                      {request.isUrgent && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                          URGENT
                        </span>
                      )}
                    </div>

                    {/* Sales Rep Information */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <ProfilePicture
                              src={request.sales_rep_profile_picture}
                              name={request.salesRepName}
                              size="md"
                            />
                            <h4 className="text-lg font-medium text-gray-800">{request.salesRepName}</h4>
                            {repApproved ? (
                              <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                <ShieldCheck className="w-4 h-4" />
                                <span className="text-sm font-medium">Approved Rep</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full">
                                <Shield className="w-4 h-4" />
                                <span className="text-sm font-medium">Not Approved</span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-1 text-sm text-gray-600">
                            <p><span className="font-medium">Company:</span> {request.salesRepCompany}</p>
                            {request.salesRepEmail && (
                              <p><span className="font-medium">Email:</span> {request.salesRepEmail}</p>
                            )}
                            {request.territory && (
                              <p><span className="font-medium">Territory:</span> {request.territory}</p>
                            )}
                          </div>

                          {/* Products */}
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Products:</p>
                            <div className="flex flex-wrap gap-2">
                              {request.products.map((product, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                  {product}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Approval Checkbox */}
                        {!repApproved && (
                          <div className="ml-4">
                            <button
                              onClick={() => addToApprovedList(request)}
                              className="flex items-center space-x-2 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 rounded-lg transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                              <span className="text-sm font-medium">Add to Approved List</span>
                            </button>
                          </div>
                        )}

                        {repApproved && (
                          <div className="ml-4">
                            <button
                              onClick={() => removeFromApprovedList(request.salesRepEmail, request.salesRepName)}
                              className="flex items-center space-x-2 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                              <span className="text-sm font-medium">Remove from List</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Restaurant Information */}
                    {request.restaurant && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Utensils className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">Restaurant:</span>
                            <span className="text-sm text-blue-700">{request.restaurant}</span>
                          </div>
                          {savedOrdersByRequest[request.requestId] && (
                            <SavedOrderIndicator
                              count={savedOrdersByRequest[request.requestId].orders.length}
                              className="ml-2"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Saved Order Section */}
                    {savedOrdersByRequest[request.requestId] && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start space-x-2 mb-3">
                          <Utensils className="w-5 h-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-green-800 mb-1">Your Usual Order</h4>
                            <p className="text-xs text-green-700 mb-2">
                              {savedOrdersByRequest[request.requestId].defaultOrder.order_template_name}
                              {savedOrdersByRequest[request.requestId].defaultOrder.average_order_size && (
                                <span className="ml-2">
                                  (Avg: ${savedOrdersByRequest[request.requestId].defaultOrder.average_order_size})
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-green-800">
                            Order Details (Edit if needed)
                          </label>
                          <textarea
                            value={
                              editedOrders[request.requestId] !== undefined
                                ? editedOrders[request.requestId]
                                : savedOrdersByRequest[request.requestId].defaultOrder.order_items
                            }
                            onChange={(e) => {
                              setEditedOrders({
                                ...editedOrders,
                                [request.requestId]: e.target.value
                              });
                            }}
                            className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white font-mono"
                            rows={6}
                            placeholder="Order details..."
                          />

                          {savedOrdersByRequest[request.requestId].defaultOrder.order_notes && (
                            <div className="mt-2 text-xs text-green-700 bg-green-100 rounded p-2">
                              <span className="font-medium">Special Instructions: </span>
                              {savedOrdersByRequest[request.requestId].defaultOrder.order_notes}
                            </div>
                          )}

                          {/* Checkbox to update saved order */}
                          {editedOrders[request.requestId] !== undefined &&
                           editedOrders[request.requestId] !== savedOrdersByRequest[request.requestId].defaultOrder.order_items && (
                            <label className="flex items-center space-x-2 mt-3 text-xs text-green-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={updateSavedOrder[request.requestId] || false}
                                onChange={(e) => {
                                  setUpdateSavedOrder({
                                    ...updateSavedOrder,
                                    [request.requestId]: e.target.checked
                                  });
                                }}
                                className="rounded border-green-400 text-green-600 focus:ring-green-500"
                              />
                              <span className="font-medium">Update my saved order with these changes</span>
                            </label>
                          )}

                          {/* Other saved orders dropdown */}
                          {savedOrdersByRequest[request.requestId].orders.length > 1 && (
                            <div className="mt-3 pt-3 border-t border-green-200">
                              <label className="block text-xs font-medium text-green-800 mb-2">
                                Or choose a different saved order:
                              </label>
                              <select
                                onChange={(e) => {
                                  const selectedOrder = savedOrdersByRequest[request.requestId].orders.find(
                                    o => o.id === e.target.value
                                  );
                                  if (selectedOrder) {
                                    setEditedOrders({
                                      ...editedOrders,
                                      [request.requestId]: selectedOrder.order_items
                                    });
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                              >
                                <option value="">Select a different order...</option>
                                {savedOrdersByRequest[request.requestId].orders
                                  .filter(o => o.id !== savedOrdersByRequest[request.requestId].defaultOrder.id)
                                  .map(order => (
                                    <option key={order.id} value={order.id}>
                                      {order.order_template_name}
                                      {order.average_order_size && ` (Avg: $${order.average_order_size})`}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Booking Notes */}
                    {request.bookingNotes && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                        <div className="flex items-start space-x-2">
                          <FileText className="w-4 h-4 text-purple-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-purple-800 mb-1">Booking Notes:</p>
                            <p className="text-sm text-purple-700 whitespace-pre-wrap">{request.bookingNotes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Special Requests */}
                    {request.specialRequests && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <p className="text-sm">
                          <span className="font-medium text-yellow-800">Special Notes:</span>
                          <span className="text-yellow-700 ml-2">{request.specialRequests}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Smart Suggestions Banner */}
                {suggestions?.contextual && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-tour="smart-suggestions">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Smart Suggestions</span>
                        <InfoTooltip
                          content="AI analyzes your meeting history to provide intelligent recommendations and pre-written response templates."
                          title="Smart Suggestions"
                        />
                        {suggestions.contextual.recommendedAction && (
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            suggestions.contextual.recommendedAction === 'approve'
                              ? 'bg-green-100 text-green-800'
                              : suggestions.contextual.recommendedAction === 'reschedule'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            Suggested: {suggestions.contextual.recommendedAction}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleSmartResponse(request.requestId)}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span className="text-sm">Smart Response</span>
                        {showingSmart ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>

                    {suggestions.contextual.reasoning.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-blue-700">
                          {suggestions.contextual.reasoning.join(' • ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Smart Response Panel */}
                {showingSmart && suggestions && (
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Pre-written Response Templates
                    </h4>

                    {/* Template Categories */}
                    <div className="space-y-3 mb-4">
                      {/* Approval Templates */}
                      <div>
                        <h5 className="text-sm font-medium text-green-700 mb-2">Approval Options</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <button
                            onClick={() => selectTemplate(request.requestId, 'approval', 'quick')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'approval.quick'
                                ? 'bg-green-50 border-green-300 text-green-800'
                                : 'bg-white border-gray-200 hover:bg-green-50'
                            }`}
                          >
                            Quick Approval
                          </button>
                          <button
                            onClick={() => selectTemplate(request.requestId, 'approval', 'personalized')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'approval.personalized'
                                ? 'bg-green-50 border-green-300 text-green-800'
                                : 'bg-white border-gray-200 hover:bg-green-50'
                            }`}
                          >
                            Personalized
                          </button>
                          <button
                            onClick={() => selectTemplate(request.requestId, 'approval', 'withPreferences')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'approval.withPreferences'
                                ? 'bg-green-50 border-green-300 text-green-800'
                                : 'bg-white border-gray-200 hover:bg-green-50'
                            }`}
                          >
                            With Preferences
                          </button>
                        </div>
                      </div>

                      {/* Decline Templates */}
                      <div>
                        <h5 className="text-sm font-medium text-red-700 mb-2">Decline Options</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <button
                            onClick={() => selectTemplate(request.requestId, 'decline', 'polite')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'decline.polite'
                                ? 'bg-red-50 border-red-300 text-red-800'
                                : 'bg-white border-gray-200 hover:bg-red-50'
                            }`}
                          >
                            Polite Decline
                          </button>
                          <button
                            onClick={() => selectTemplate(request.requestId, 'decline', 'withAlternatives')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'decline.withAlternatives'
                                ? 'bg-red-50 border-red-300 text-red-800'
                                : 'bg-white border-gray-200 hover:bg-red-50'
                            }`}
                          >
                            With Alternatives
                          </button>
                          <button
                            onClick={() => selectTemplate(request.requestId, 'decline', 'busy')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'decline.busy'
                                ? 'bg-red-50 border-red-300 text-red-800'
                                : 'bg-white border-gray-200 hover:bg-red-50'
                            }`}
                          >
                            Schedule Conflict
                          </button>
                        </div>
                      </div>

                      {/* Reschedule Templates */}
                      <div>
                        <h5 className="text-sm font-medium text-yellow-700 mb-2">Reschedule Options</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <button
                            onClick={() => selectTemplate(request.requestId, 'reschedule', 'timeConflict')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'reschedule.timeConflict'
                                ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                                : 'bg-white border-gray-200 hover:bg-yellow-50'
                            }`}
                          >
                            Time Conflict
                          </button>
                          <button
                            onClick={() => selectTemplate(request.requestId, 'reschedule', 'preferencesBased')}
                            className={`text-left p-2 text-sm rounded border transition-colors ${
                              selectedTemplate === 'reschedule.preferencesBased'
                                ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                                : 'bg-white border-gray-200 hover:bg-yellow-50'
                            }`}
                          >
                            Preference Based
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Message Preview & Edit */}
                    {customMessage && (
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Message Preview</label>
                            <button
                              onClick={copyTemplate}
                              className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              <Copy className="w-4 h-4" />
                              <span className="text-xs">Copy</span>
                            </button>
                          </div>
                          <textarea
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows={4}
                            placeholder="Edit your message here..."
                          />
                        </div>

                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={() => setShowSmartResponse(null)}
                            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSmartDecline(request.requestId, customMessage)}
                            disabled={actionInProgress === request.requestId}
                            className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            Send & Decline
                          </button>
                          <button
                            onClick={() => handleSmartApprove(request.requestId, customMessage)}
                            disabled={actionInProgress === request.requestId}
                            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            Send & Approve
                          </button>
                        </div>
                      </div>
                    )}

                    {suggestions.timeSlots && suggestions.timeSlots.suggestedTimes && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Your Preferred Times</h5>
                        <div className="flex flex-wrap gap-2">
                          {suggestions.timeSlots.suggestedTimes.slice(0, 4).map(time => (
                            <span
                              key={time}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {time}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {suggestions.timeSlots.reason}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => handleDeclineRequest(request.requestId, request.salesRepName)}
                    disabled={actionInProgress === request.requestId}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    <span>Decline</span>
                  </button>
                  <button
                    onClick={() => handleApproveRequest(request.requestId)}
                    disabled={actionInProgress === request.requestId}
                    className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Approve Meeting</span>
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* Notifications Section */}
      {notifications.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Recent Notifications</h3>
              <p className="text-gray-600 text-sm mt-1">
                Cancellations, confirmations, and other updates
              </p>
            </div>
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              {notifications.length} unread
            </span>
          </div>

          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-white rounded-lg shadow-md border-l-4 border-yellow-500 p-4 transition-all hover:shadow-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Bell className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium text-gray-800">
                        {notification.type === 'meeting_cancelled_by_customer' ? 'Meeting Cancelled' :
                         notification.type === 'meeting_cancelled_by_sales' ? 'Meeting Cancelled' :
                         notification.type === 'meeting_approved' ? 'Meeting Confirmed' :
                         notification.type === 'meeting_declined' ? 'Meeting Declined' :
                         'Notification'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">
                      {notification.message}
                    </p>
                  </div>
                  <button
                    onClick={() => markNotificationAsRead(notification.id)}
                    className="ml-4 px-3 py-1 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-lg transition-colors"
                  >
                    Mark as Read
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Dropdowns - Matching Sales Rep View */}
      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        {/* Time Period Dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">View:</label>
          <select
            value={viewType}
            onChange={(e) => {
              setViewType(e.target.value);
              setClickedDay(null); // Clear clicked day when changing view
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="daily">Day</option>
            <option value="weekly">Week</option>
            <option value="monthly">Month</option>
          </select>
        </div>

        {/* Status Filter Dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Show:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Meetings</option>
            <option value="confirmed">Active (Confirmed)</option>
            <option value="active-pending">Active + Pending</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled / Declined</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Meetings Section */}
      <div className="mt-6" data-section="upcoming-meetings">
        {(() => {
          const filteredMeetings = getFilteredMeetings();
          const dateRangeText = getDateRangeText();

          return (
            <>
              {/* Section Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {clickedDay
                      ? clickedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                      : dateRangeText
                    }
                  </h3>
                  <p className="text-gray-600 text-sm mt-1 flex items-center gap-2">
                    {clickedDay ? (
                      <>
                        <span>{filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''} on this day</span>
                        <button
                          onClick={() => setClickedDay(null)}
                          className="px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 rounded transition-colors"
                        >
                          Show Full {viewType === 'weekly' ? 'Week' : 'Month'}
                        </button>
                      </>
                    ) : (
                      <span>
                        {filteredMeetings.filter(m => m.status === 'confirmed').length} Confirmed •{' '}
                        {filteredMeetings.filter(m => m.status === 'pending').length} Pending •{' '}
                        {filteredMeetings.filter(m => m.status === 'completed').length} Completed
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {filteredMeetings.length > 0 && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* Meetings Grid */}
              {filteredMeetings.length === 0 ? (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-lg font-medium text-gray-600 mb-2">No Meetings</h4>
                  <p className="text-gray-500">
                    {clickedDay
                      ? 'No meetings scheduled for this day.'
                      : `No meetings found with the selected filters for this ${viewType === 'daily' ? 'day' : viewType === 'weekly' ? 'week' : 'month'}.`
                    }
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {filteredMeetings.map((meeting) => (
                    <EnhancedMeetingCard
                      key={meeting.id}
                      meeting={meeting}
                      onClick={() => {
                        if (setSelectedMeetingId && setCurrentView) {
                          setSelectedMeetingId(meeting.id);
                          setCurrentView('meeting-detail');
                        }
                      }}
                      userType="customer"
                      showRSVP={true}
                      onAccept={handleAcceptRSVP}
                      onDecline={handleDeclineRSVP}
                      isHighlighted={clickedDay &&
                        (() => {
                          const meetingDate = parseMeetingDate(meeting);
                          return meetingDate.toDateString() === clickedDay.toDateString();
                        })()
                      }
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Quick Stats */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 w-full max-w-full">
        <div className="bg-blue-50 rounded-lg p-3 md:p-4 min-w-0">
          <p className="text-xs sm:text-sm text-blue-600 mb-1 truncate">
            {viewType === 'daily' ? 'Today' : viewType === 'weekly' ? 'This Week' : 'This Month'}
          </p>
          <p className="text-xl sm:text-2xl font-bold text-blue-900 truncate">
            {getFilteredMeetings().length} meeting{getFilteredMeetings().length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 md:p-4 min-w-0">
          <p className="text-xs sm:text-sm text-green-600 mb-1 truncate">Unique Reps</p>
          <p className="text-xl sm:text-2xl font-bold text-green-900 truncate">
            {new Set(getFilteredMeetings().map(m => m.salesRepId || m.sales_rep_id).filter(Boolean)).size}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 md:p-4 min-w-0">
          <p className="text-xs sm:text-sm text-purple-600 mb-1 truncate">Companies</p>
          <p className="text-xl sm:text-2xl font-bold text-purple-900 truncate">
            {new Set(getFilteredMeetings().map(m => {
              if (m.salesRepData?.company) return m.salesRepData.company;
              if (m.sales_rep_company) return m.sales_rep_company;
              if (m.salesRepCompany) return m.salesRepCompany;
              return null;
            }).filter(Boolean)).size}
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MeetingRequestsView);